/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import classnames from 'classnames';

import { IRefinerToggleProps, RefinerToggleComponent } from './refiner-toggle.component';
import { IRefinerChoiceSummaryProps, RefinerChoiceSummaryComponent } from './refiner-choice-summary.component';
import { IRefinerSortProps, RefinerSortComponent } from './refiner-sort.component';

/**
 * Class names used in component.
 */
const componentClassName = 'msc-refiner-header';
const choiceSummaryWrapperClassName = `${componentClassName}__wrapper-choice-summary`;
const moreActionsWrapperClassName = `${componentClassName}__wrapper-more-actions`;
const extraActionsWrapperClassName = `${componentClassName}__wrapper-extra-actions`;

/**
 * IRefinerHeaderComponent interface.
 */
interface IRefinerHeaderComponent extends IComponent<IRefinerHeaderProps> {}

/**
 * IRefinerHeaderProps interface.
 */
export interface IRefinerHeaderProps extends IComponentProps<{}> {
    className?: string;
    refinerToggleProps?: IRefinerToggleProps;
    refinerChoiceSummaryProps?: IRefinerChoiceSummaryProps;
    refinerSortProps?: IRefinerSortProps;
    extraActions?: React.ReactNode;
}

/**
 * RefinerHeader component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const RefinerHeaderFunctionalComponent: React.FC<IRefinerHeaderProps> = (props: IRefinerHeaderProps) => {
    const { className, extraActions } = props;

    return (
        <Node className={classnames(className, componentClassName)}>
            {props.refinerToggleProps && <RefinerToggleComponent {...props.refinerToggleProps} />}
            {props.refinerChoiceSummaryProps && (
                <div className={choiceSummaryWrapperClassName}>
                    <RefinerChoiceSummaryComponent {...props.refinerChoiceSummaryProps} />
                </div>
            )}
            <div className={moreActionsWrapperClassName}>
                {props.refinerSortProps && <RefinerSortComponent {...props.refinerSortProps} />}
                {extraActions && <div className={extraActionsWrapperClassName}>{extraActions}</div>}
            </div>
        </Node>
    );
};

/**
 * RefinerHeader component.
 */
export const RefinerHeaderComponent: React.FunctionComponent<IRefinerHeaderProps> = msdyn365Commerce.createComponent<
    IRefinerHeaderComponent
>('Refiner-Header', {
    component: React.memo(RefinerHeaderFunctionalComponent)
});
